/**
 * Página para crear o editar modificador (similar a categoría/producto)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  TextField,
  BlockStack,
  Box,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@/lib/polaris';
import { ProductIcon } from '@/lib/polaris-icons';
import { supabase } from '@/lib/supabase';
import { fetchModifierById, fetchModifierBySlug, updateModifierByIdentity } from '@/lib/modifiersApi';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { ROUTES, storePath, slugifyModifierName } from '@/config/routes.config';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { useUnsavedChanges } from '@/contexts/UnsavedChangesContext';
import { getStatsPageCache, setStatsPageCache, adminDetailCacheKey } from '@/lib/statsPageCache';

interface ModifierFormData {
  name: string;
  price_adjustment: number;
}

export default function ModifierEditPage() {
  const { storeSlug, slug: urlSlug, id: legacyId } = useParams<{ storeSlug?: string; slug?: string; id?: string }>();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();
  const isNew = !urlSlug && !legacyId;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ModifierFormData>({
    name: '',
    price_adjustment: 0,
  });
  const [initialFormData, setInitialFormData] = useState<ModifierFormData | null>(null);
  const [originalSlug, setOriginalSlug] = useState<string>('');

  const toModifiers = useCallback(
    () => navigate(storeSlug ? storePath(storeSlug, ROUTES.PRODUCTS_MODIFIERS) : ROUTES.PRODUCTS_MODIFIERS),
    [navigate, storeSlug]
  );

  useEffect(() => {
    if (isNew && !loading && initialFormData === null) {
      setInitialFormData(formData);
    }
  }, [isNew, loading, initialFormData, formData]);

  const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

  const loadModifier = useCallback(async (revalidateOnly = false) => {
    if (!tenantId || (!urlSlug && !legacyId)) return;

    const cacheIdentity = legacyId
      ? `id:${legacyId}`
      : `slug:${decodeURIComponent(urlSlug ?? '')}`;
    const cacheKey = adminDetailCacheKey('modifier', tenantId, cacheIdentity);

    if (!revalidateOnly) {
      const cached = getStatsPageCache<{ row: { name: string; price_adjustment: number; slug?: string } }>(cacheKey);
      if (cached?.row) {
        const loaded = {
          name: cached.row.name || '',
          price_adjustment: cached.row.price_adjustment ?? 0,
        };
        setFormData(loaded);
        setInitialFormData(loaded);
        setOriginalSlug(cached.row.slug || '');
        setLoading(false);
      }
    }

    if (!revalidateOnly) setLoading(true);
    try {
      // Ruta legacy por id (o :slug que es un UUID): cargar y redirigir a URL con slug
      const loadByLegacyId = legacyId || (urlSlug && isUuid(urlSlug) ? urlSlug : null);
      if (loadByLegacyId) {
        const legacyData = await fetchModifierById(tenantId, loadByLegacyId);

        if (!legacyData) {
          toast.error('Modificador no encontrado');
          toModifiers();
          setLoading(false);
          return;
        }
        let slug = legacyData.slug;
        if (!slug) {
          slug = slugifyModifierName(legacyData.name || '');
          // Ya está probado que la fila es del tenant (fetchModifierById scopea por el join),
          // así que alcanza con el id.
          await supabase.from('product_modifiers').update({ slug }).eq('id', loadByLegacyId);
        }
        const target = storeSlug
          ? storePath(storeSlug, `/products/modifiers/${encodeURIComponent(slug)}/edit`)
          : `/products/modifiers/${encodeURIComponent(slug)}/edit`;
        navigate(target, { replace: true });
        setLoading(false);
        return;
      }

      const data = await fetchModifierBySlug(tenantId, decodeURIComponent(urlSlug!));

      if (!data) {
        toast.error('Modificador no encontrado');
        toModifiers();
        setLoading(false);
        return;
      }

      const loaded: ModifierFormData = {
        name: data.name || '',
        price_adjustment: data.price_adjustment ?? 0,
      };
      setFormData(loaded);
      setInitialFormData(loaded);
      setOriginalSlug(data.slug || '');
      setStatsPageCache(cacheKey, {
        row: {
          name: data.name || '',
          price_adjustment: data.price_adjustment ?? 0,
          slug: data.slug || '',
        },
      });
    } catch (e: unknown) {
      toast.error('Error al cargar modificador', e instanceof Error ? e.message : 'Error desconocido');
      toModifiers();
    } finally {
      setLoading(false);
    }
  }, [urlSlug, legacyId, tenantId, storeSlug, toModifiers, toast]);

  useEffect(() => {
    if (!isNew && tenantId && (urlSlug || legacyId)) {
      loadModifier();
    }
  }, [urlSlug, legacyId, tenantId, isNew, loadModifier]);

  const handleSave = useCallback(async () => {
    if (!formData.name.trim() || !tenantId) return;
    // Mismo guard que en Categorías: `saving` sin lector = doble clic = registro duplicado.
    if (saving) return;

    setSaving(true);
    try {
      const newSlug = slugifyModifierName(formData.name.trim());

      if (isNew) {
        const { data: sampleProduct } = await supabase
          .from('products')
          .select('id')
          .eq('tenant_id', tenantId)
          .eq('is_active', true)
          .limit(1)
          .single();

        if (!sampleProduct) {
          toast.error('Primero debes crear al menos un producto');
          setSaving(false);
          return;
        }

        const { error } = await supabase.from('product_modifiers').insert({
          product_id: sampleProduct.id,
          name: formData.name.trim(),
          slug: newSlug,
          price_adjustment: formData.price_adjustment,
          is_required: false,
        });

        if (error) throw error;
        toast.success('Modificador creado', formData.name);
      } else {
        const affected = await updateModifierByIdentity(tenantId, {
          originalSlug,
          name: formData.name.trim(),
          slug: newSlug,
          priceAdjustment: formData.price_adjustment,
        });
        // Antes, si el update no tocaba ninguna fila (p. ej. la lista de productos venía
        // truncada en 1000), igual se cantaba éxito y el usuario se iba creyendo que guardó.
        if (affected === 0) {
          toast.error('No se guardó', 'No se encontró el modificador. Recargá la página.');
          return;
        }
        toast.success('Modificador actualizado', formData.name);
      }

      toModifiers();
    } catch (e: unknown) {
      toast.error(isNew ? 'Error al crear modificador' : 'Error al actualizar modificador', e instanceof Error ? e.message : 'Error desconocido');
    } finally {
      setSaving(false);
    }
  }, [formData, tenantId, isNew, originalSlug, toModifiers, toast]);

  const hasUnsavedChanges =
    initialFormData !== null &&
    (formData.name !== initialFormData.name || formData.price_adjustment !== initialFormData.price_adjustment);

  const handleDiscard = useCallback(() => {
    if (initialFormData === null) return;
    setFormData(initialFormData);
    toast.info('Cambios descartados', 'Se han descartado todos los cambios');
  }, [initialFormData, toast]);

  const { setUnsaved } = useUnsavedChanges();
  const handleSaveRef = useRef(handleSave);
  const handleDiscardRef = useRef(handleDiscard);
  handleSaveRef.current = handleSave;
  handleDiscardRef.current = handleDiscard;
  useEffect(() => {
    if (!hasUnsavedChanges) {
      setUnsaved(null);
      return () => setUnsaved(null);
    }
    setUnsaved({
      onSave: () => handleSaveRef.current(),
      onDiscard: () => handleDiscardRef.current(),
    });
    return () => setUnsaved(null);
  }, [hasUnsavedChanges, setUnsaved]);

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={ProductIcon}
          title={isNew ? 'Crear modificador' : 'Editar modificador'}
          onBack={toModifiers}
          backAccessibilityLabel="Volver a Modificadores"
        />
      }
    >
      {loading ? (
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <SkeletonDisplayText size="large" />
              <SkeletonBodyText lines={8} />
            </BlockStack>
          </Box>
        </Card>
      ) : (
      <BlockStack gap="500">
        <Card>
          <BlockStack gap="400">
            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
              placeholder="ej: Extra Queso, Sin Azúcar…"
              autoComplete="off"
              requiredIndicator
              autoFocus
            />
            <TextField
              label="Ajuste de Precio"
              type="number"
              value={formData.price_adjustment.toString()}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, price_adjustment: parseFloat(value) || 0 }))
              }
              placeholder="0.00"
              prefix="S/"
              autoComplete="off"
              helpText="Ingresa un valor positivo para sumar o negativo para restar al precio base"
            />
          </BlockStack>
        </Card>
      </BlockStack>
      )}
    </Page>
  );
}
